import React, {useEffect, useRef, useState} from 'react';
import {Template} from "../../../../components/template/template.jsx";
import InputForm from "../../../../components/input/input-form.jsx";
import {Card} from "../../../../components/card/card.jsx";
import Button from "../../../../components/button/button.jsx";
import {useParams} from "react-router";
import {FaPlus} from "react-icons/fa";
import {navigateHook} from "../../../../hooks/use-navigate.js";
import {useQuery} from "@tanstack/react-query";
import projectUtils from "../../../../utils/project-utils.js";
import Swal from "sweetalert2";
import VehicleClassesService from "../../../../services/web/vehicle-classes-service.js";
import {useGlobalKey} from "../../../../hooks/use-global-key.js";
import AssetPlacesService from "../../../../services/web/asset-places-service.js";
import Alert from "../../../../components/alert/alert.jsx";
import {useRequiredFields} from "../../../../hooks/use-required-fields.js";

const VehicleClassesForm = () => {
    const navigate = navigateHook();

    const {id} = useParams()
    const [isLoading, setIsLoading] = useState(false);
    const [state, setState] = useState({});
    const nameReference = useRef(null);
    const {clearRequiredFieldError, getRequiredFieldProps, validateRequiredFields} = useRequiredFields();

    const { status, refetch } = useQuery({
        queryFn: () =>  VehicleClassesService.get(id).then((data) => {
            setState(data)
            return []
        }),
        queryKey: ['vehicleClasses'],
        enabled: !!id
    });

    const {data: hasAssociated} = useQuery({
        queryKey: ['vehicle-class-has-associated'],
        queryFn: async () => {
            const {hasAssociated} = await VehicleClassesService.hasAssociated(id)
            return hasAssociated
        },
        enabled: !!id
    });

    useEffect(() => {
        if (id) {
            if (status === 'success') setIsLoading(false)
            else setIsLoading(true)
        }
    }, [status]);

    const onChange = ({target: {value, id}}) => {
        setState(prev => ({...prev, [id]: value}));
        clearRequiredFieldError(id, value);
    }

    const save = () => {
        if (!validateRequiredFields([{id: "name", value: state.name, reference: nameReference}])) return;

        const formData = new FormData();
        const form = { name: state.name };

        Object.keys(form).forEach(key => {
            formData.append(key, form[key])
        });

        if (id) formData.append('_method', 'PUT');

        const response = id ? VehicleClassesService.update(id, formData) : VehicleClassesService.create(formData);
        response
            .then(({data}) => {
                projectUtils.success(!id);

                if (id) {
                    refetch();
                    return;
                }

                navigateToEdit(data.id);
            })
            .catch((error) => {
                console.error(error);

                const {message} = error.response.data;

                let errMessage = message ?? "Não foi possível salvar.";

                if(errMessage === "name.already.used") errMessage = "Este \"Nome\" já está sendo utilizado."
                projectUtils.swalErrorMessage("", errMessage, "Ok");
            })

    }

    const destroy = () => {
        Swal.fire({
            title: "Excluir",
            text: `Excluir a classe "${state.name}"?`,
            icon: "warning",
            confirmButtonText: "Sim",
            confirmButtonColor: "#063A6C",
            cancelButtonText: "Não",
            cancelButtonColor: "#c10007",
            showCancelButton: true,
            allowOutsideClick: false,
            allowEscapeKey: false,
            reverseButtons: true
        }).then(({isConfirmed}) => {
            if (isConfirmed) {
                VehicleClassesService.destroy(id)
                    .then(() => {
                        projectUtils.success(true, "Excluído com sucesso");
                        navigateToList();
                    }).catch((error) => {
                        console.error(error);

                        const { message } = error.response.data;
                        let errMessage = message ?? "Não foi possível salvar.";

                        projectUtils.swalErrorMessage("", errMessage, "Ok");
                    });
            }
        });
    }

    const navigateToList = () => {
        let isBlank = false;
        let urlDev = `/vehicle-classes`;
        navigate(urlDev, isBlank);
    }

    const newPlace = () => {
        let isBlank = false;
        let urlDev = `/vehicle-classes/form`;
        navigate(urlDev, isBlank);
    }

    const navigateToEdit = (id) => {
        let isBlank = false;
        let urlDev = `/vehicle-classes/form/${id}`;
        navigate(urlDev, isBlank);
    }

    return (
        <Template isLoading={isLoading}>
            <Template.Header>
                <Template.Title browserTitle="Classe de veículos">Classe de veículo</Template.Title>

                { id &&
                    <Button target={"primary-outline"} onClick={() => newPlace()}>
                        <FaPlus/>
                        Adicionar classe
                    </Button>
                }
            </Template.Header>

            <Template.Body>
                <Card>
                    <Card.Body>
                        <div className="grid grid-cols-12 gap-4">
                            <div className="col-span-12">
                                <InputForm
                                    id="name"
                                    label="Nome"
                                    value={state.name}
                                    onChange={onChange}
                                    reference={nameReference}
                                    {...getRequiredFieldProps("name")}
                                />
                            </div>
                        </div>
                    </Card.Body>
                </Card>

                <Card className={"mt-2"}>
                    <Card.Body>
                        <div className="flex justify-end items-center gap-2">
                            { hasAssociated && <Alert target="warning">Não é possível excluir, pois existem veículos dependentes da classe.</Alert> }
                            { id && <Button disabled={hasAssociated} target={"danger"} onClick={destroy} actionFeedbackType='delete'>Excluir</Button> }

                            <Button onClick={save} actionFeedbackType='save'>
                                Salvar
                            </Button>
                        </div>
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    );
};

export default VehicleClassesForm;